import React, { Children, cloneElement, forwardRef, isValidElement } from 'react';
import { Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { CustomizableComponent } from './CustomizableComponent';
import { ValueBox } from './SettingGroup';

const useStyles = makeStyles((theme) => ({
  dashboard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    '& > $collection': {
      marginTop: 0
    },
    '& > $collection:has([data-info-expanded="true"])': {
      gridColumn: '1 / -1'
    },
    '& > $collection[data-info-paired="true"]': {
      gridColumn: '1 / -1'
    },
    '& > $collection[data-info-orphan="true"]': {
      gridColumn: '1 / -1'
    },
    '@media (max-width: 1199px)': {
      gridTemplateColumns: 'minmax(0, 1fr)'
    }
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)'
    }
  },
  collection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    alignContent: 'start',
    alignItems: 'start',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.outerContainer.background,
    boxShadow: '0 3px 2px ' + theme.palette.innerContainer.shadow,
    '&[data-info-paired="true"]': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    },
    '@media (min-width: 1200px)': {
      '&[data-info-orphan="true"]:not(:has([data-info-expanded="true"]))': {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
      }
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      '&[data-info-paired="true"]': {
        gridTemplateColumns: 'minmax(0, 1fr)'
      },
      padding: theme.spacing(1)
    }
  },
  group: {
    minWidth: 0,
    overflow: 'hidden',
    backgroundColor: theme.palette.innerContainer.background,
    color: theme.palette.fontcolor,
    boxShadow: '0 3px 2px ' + theme.palette.innerContainer.shadow,
  },
  heading: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.innerContainer2.background}`,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    textTransform: 'capitalize'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, auto) auto',
    alignItems: 'center',
    columnGap: theme.spacing(2),
    minHeight: 48,
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.innerContainer2.background}`,
    '&[data-customization-position="row-gutter"]': {
      paddingLeft: theme.spacing(5)
    },
    '&:last-child': {
      borderBottom: 0
    },
    '@media (min-width: 1200px)': {
      '&[data-info-fixed-visualization="true"]': {
        gridTemplateColumns: 'minmax(110px, 1fr) 200px 250px'
      }
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr) auto'
    }
  },
  label: {
    minWidth: 0,
    overflowWrap: 'anywhere'
  },
  value: {
    textAlign: 'right',
    whiteSpace: 'nowrap',
    '& label': {
      fontSize: '1.1rem !important'
    }
  },
  metricValue: {
    textAlign: 'right',
    whiteSpace: 'nowrap'
  },
  valueBlock: {
    gridColumn: 3,
    display: 'flex',
    minWidth: 0,
    flexDirection: 'column',
    alignItems: 'flex-end',
    '@media (min-width: 1200px)': {
      '[data-info-fixed-visualization="true"] &': {
        gridColumn: 2,
        gridRow: 1
      }
    },
    [theme.breakpoints.down('xs')]: {
      gridColumn: 2
    }
  },
  setpoint: {
    marginBottom: theme.spacing(0.25),
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    lineHeight: 1.2,
    textAlign: 'right',
    whiteSpace: 'nowrap'
  },
  inactiveSetpoint: {
    opacity: 0.55,
    textDecoration: 'line-through'
  },
  valueDetail: {
    display: 'flex',
    justifyContent: 'flex-end',
    minWidth: 0,
    width: '100%',
    '[data-info-fixed-visualization="true"] & > *': {
      minWidth: 0,
      maxWidth: '100%'
    }
  },
  visualization: {
    gridColumn: 2,
    minWidth: 0,
    width: 120,
    '[data-info-fixed-visualization="true"] &': {
      width: '100%'
    },
    '@media (min-width: 1200px)': {
      '[data-info-fixed-visualization="true"] &': {
        gridColumn: 3,
        gridRow: 1
      }
    },
    [theme.breakpoints.down('xs')]: {
      gridColumn: '1 / -1',
      gridRow: 2,
      width: '100%'
    }
  },
  detail: {
    gridColumn: '1 / -1',
    minWidth: 0,
    overflow: 'hidden'
  },
  metricBox: {
    '&&': {
      paddingRight: theme.spacing(3)
    },
    [theme.breakpoints.down('xs')]: {
      '&&': {
        paddingRight: theme.spacing(1.5)
      }
    }
  },
  metricHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(0.5)
  },
  metricValueBlock: {
    display: 'flex',
    minWidth: 0,
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right'
  },
  error: {
    boxShadow: 'inset 3px 0 0 #F54927'
  },
  rowsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(var(--info-value-column-count), minmax(0, 1fr))',
    '& > *': {
      minWidth: 0
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)'
    }
  }
}));

export function InfoValueList({ children }) {
  const classes = useStyles();

  return <div className={classes.grid}>{Children.toArray(children).filter(Boolean)}</div>;
}

export function InfoValueBox({
  children,
  className = '',
  controller,
  id,
  label,
  setpoint,
  setpointDisabled = false,
  style,
  valueDetail,
  visualization
}) {
  const classes = useStyles();

  return (
    <ValueBox
      className={`${classes.metricBox} ${className}`}
      controller={controller}
      id={id}
      style={style}
    >
      <div className={classes.metricHeader}>
        <div className={classes.label}>{label}</div>
        {setpoint != null && (
          <div
            className={`${classes.setpoint} ${setpointDisabled ? classes.inactiveSetpoint : ''}`}
            data-info-setpoint-disabled={setpointDisabled ? 'true' : undefined}
          >
            {setpoint}
          </div>
        )}
      </div>
      <div className={classes.metricValueBlock} data-info-value-block>
        <div className={classes.metricValue} data-info-metric-value>
          {children}
        </div>
        {valueDetail && <div className={classes.valueDetail}>{valueDetail}</div>}
      </div>
      {visualization}
    </ValueBox>
  );
}

export function InfoValueDashboard({ children }) {
  const classes = useStyles();
  const items = Children.toArray(children).filter(Boolean);
  if (items.length === 0) return null;

  const orphanIndexes = new Set();
  let pendingCompactIndex = null;

  items.forEach((child, index) => {
    if (!isValidElement(child)) return;

    if (child.props.paired) {
      if (pendingCompactIndex !== null) orphanIndexes.add(pendingCompactIndex);
      pendingCompactIndex = null;
      return;
    }

    pendingCompactIndex = pendingCompactIndex === null ? index : null;
  });

  if (pendingCompactIndex !== null) orphanIndexes.add(pendingCompactIndex);

  return (
    <div className={classes.dashboard} data-info-value-dashboard>
      {items.map((child, index) =>
        isValidElement(child) ? cloneElement(child, { orphan: orphanIndexes.has(index) }) : child
      )}
    </div>
  );
}

export const InfoValueCollection = forwardRef(function InfoValueCollection(
  { children, orphan = false, paired = false, reference },
  ref
) {
  const classes = useStyles();

  const groups = Children.toArray(children).filter(Boolean);
  if (groups.length === 0) return null;

  return (
    <div
      ref={ref || reference}
      className={classes.collection}
      data-info-orphan={orphan ? 'true' : undefined}
      data-info-paired={paired ? 'true' : undefined}
      data-info-value-collection
    >
      {groups}
    </div>
  );
});

export function CustomizableInfoValueCollection({
  children,
  controller,
  id,
  orphan = false,
  paired = false,
  parent
}) {
  return (
    <CustomizableComponent
      controller={controller}
      parent={parent}
      id={id}
      visibilityIconPosition="top-left"
      zIndex={1}
    >
      <InfoValueCollection orphan={orphan} paired={paired}>
        {children}
      </InfoValueCollection>
    </CustomizableComponent>
  );
}

export function splitInfoRowsIntoColumns(rows, capacity, maxColumns = 2) {
  if (rows.length === 0) return [];

  const columnCount = Math.min(maxColumns, Math.ceil(rows.length / Math.max(1, capacity)));
  const rowsPerColumn = Math.ceil(rows.length / columnCount);

  return Array.from({ length: columnCount }, (_, columnIndex) =>
    rows.slice(columnIndex * rowsPerColumn, (columnIndex + 1) * rowsPerColumn)
  );
}

export function InfoValueRowsGrid({ children, columns }) {
  const classes = useStyles();
  const visibleColumns = (columns ?? [children]).filter(Boolean);

  return (
    <div
      className={classes.rowsGrid}
      data-info-value-column-count={visibleColumns.length}
      data-info-value-rows-grid
      style={{ '--info-value-column-count': visibleColumns.length }}
    >
      {visibleColumns.map((column, index) => (
        <div data-info-value-column key={`info-value-column-${index}`}>
          {Children.toArray(column).filter(Boolean)}
        </div>
      ))}
    </div>
  );
}

export const InfoValueGroup = forwardRef(function InfoValueGroup(
  { title, children, expanded = false, reference },
  ref
) {
  const classes = useStyles();
  const rows = Children.toArray(children).filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <Paper
      ref={ref || reference}
      className={classes.group}
      data-info-expanded={expanded}
      data-info-value-group
    >
      <Typography component="h3" variant="body1" className={classes.heading}>
        <span data-customization-heading style={{ alignItems: 'center', display: 'inline-flex' }}>
          {title}
        </span>
      </Typography>
      <div>{rows}</div>
    </Paper>
  );
});

export const InfoValueRow = forwardRef(function InfoValueRow(props, ref) {
  const {
    label,
    children,
    detail,
    error = false,
    reference,
    setpoint,
    valueDetail,
    visualization,
    fixedVisualization = false
  } = props;
  const classes = useStyles();

  return (
    <div
      ref={ref || reference}
      className={`${classes.row} ${error ? classes.error : ''}`}
      data-customization-position={props['data-customization-position']}
      data-info-fixed-visualization={fixedVisualization ? 'true' : undefined}
      data-info-value-row
    >
      <div className={classes.label}>{label}</div>
      {visualization && <div className={classes.visualization}>{visualization}</div>}
      <div className={classes.valueBlock} data-info-value-block>
        {setpoint && <div className={classes.setpoint}>{setpoint}</div>}
        <div className={classes.value}>{children}</div>
        {valueDetail && <div className={classes.valueDetail}>{valueDetail}</div>}
      </div>
      {detail && <div className={classes.detail}>{detail}</div>}
    </div>
  );
});

export function CustomizableInfoValueRow({
  controller,
  parent,
  id,
  extId,
  label,
  children,
  ...rowProps
}) {
  return (
    <CustomizableComponent
      controller={controller}
      parent={parent}
      id={id}
      ext_id={extId}
      visibilityIconPosition="row-gutter"
    >
      <InfoValueRow label={label} {...rowProps}>
        {children}
      </InfoValueRow>
    </CustomizableComponent>
  );
}
